import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { PlaceService } from '@app/modules/model/svcplace';
import { SmartDrawerDevice } from '@app/modules/model/smartdrawer';

import { languageService, languageSupport } from '@app/modules/common/language';
import { alertService } from '@app/modules/common/alert';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

/************************************/
/* USER COMPONENT                   */
/************************************/

@Component({
    selector: 'ui-smartdrawer-devices',
    templateUrl: 'ui-smartdrawer.html',
    styleUrls: [
      'ui-smartdrawer.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSmartDrawerDevices extends languageSupport implements OnInit, OnChanges, OnDestroy { 
    @Input('service') _service: PlaceService = null;
    @Input('editdevice') _editdevice: any = null;

    /*****************************/
    /* COMPONENT DATA SHARING    */
    /*****************************/

    private get EditDevice(){
        return this._editdevice;
    }

    DiscardChanges(){
        this._RefreshDevices();
        this.IsDirty = false;
        this.LoadSmartDrawerForm();
    }

    @Output('OnUpdate') _onupdate = new EventEmitter <any> ();
    private set EditDevice(value){
        this._editdevice = value;

        this._onupdate.next();
        this.change.markForCheck();
    }

    @Output('OnChange') _onchange = new EventEmitter <any> ();
    OnViewChanged(){
        this.change.markForCheck();
        this._onchange.emit();
    }

    get IsValid(){
        return true;
    }

    private _isdirty = false;
    get IsDirty(){
        if (!this.IsValid){
            return false;   // do not consider if its not valid
        }
        
        return this._isdirty;
    }

    set IsDirty(value){
        this._isdirty = value;
        if (value == false){
            this.SmartDrawerForm.markAsPristine();
        }
        
        this.OnViewChanged();
    }

    private _smartdrawers: Array<SmartDrawerDevice> = [];
    get PlaceSmartDrawers(){
        return this._smartdrawers || [];
    }

    /*****************************/
    /* COMPONENT DATA ACCESS     */
    /*****************************/

    get SmartDrawerModels(){
        // El submodelo de Glory (CI-5/CI-10/CI-10X/CI-15) NO se elige aquí: es
        // una propiedad del hardware de esa máquina y se configura en el backend
        // (configurador del print_service, junto a la IP del cajón). La app solo
        // conoce la familia "GLORY"; el backoffice recibe el submodelo del back.
        let _options = [
            "CASHGUARD", "CASHLOGY", "PROSEGUR", "GLORY", "CASHDRO"
        ];

        let _values = [];
        for(let _option of _options){
            _values.push({
                value: _option, text: _option
            })
        }

        return _values;
    }
    
    private _smartdrawerForm = null;
    get SmartDrawerForm(){
        if (this._smartdrawerForm == null){
            this.LoadSmartDrawerForm();
        }
        return this._smartdrawerForm;
    }

    private _getSmartDrawerModel(value){
        let _text = null;

        // Compat: modelos antiguos "GLORY CI-5/CI-10/…" (el submodelo ahora vive
        // en el configurador del print_service, no en la app) se normalizan a
        // "GLORY" para que el selector los muestre bien y no queden huérfanos.
        if (value && String(value).toUpperCase().indexOf('GLORY') === 0){
            value = 'GLORY';
        }

        for(let _option of this.SmartDrawerModels){
            if (_option.value == value){
                _text = _option.text;
            }
        }

        return { value: value, text: _text }; 
    }    
    
    private LoadSmartDrawerForm(){
        let _name = null;
        let _model = null;
    
        if (this.EditDevice){
            _name = this.EditDevice.name;
            _model = this._getSmartDrawerModel(this.EditDevice.model);
        }

        if (this._smartdrawerForm == null){
            this._smartdrawerForm = new FormGroup({
                name: new FormControl(_name, [
                    // no validators required
                ]),
                model: new FormControl(_model, [
                    Validators.required
                ])
            })
        }
        else {
            this._smartdrawerForm.patchValue({
                name: _name, 
                model: _model
            });
        }

        this._smartdrawerForm.markAsPristine();
        setTimeout(() => {
            this._smartdrawerForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    private _service_subscription = null;

    private _RefreshDevices(){
        this._smartdrawers = this._PlaceSmartDrawers;
        this.IsDirty = false;

        this.OnViewChanged();      // update devices view on parent
    }

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private get _PlaceSmartDrawers(){
        let _smartdrawers = [];
        if (this._service){
            for(let _smartdrawer of this._service.smartdrawers)
            if (_smartdrawer.IsValid){
                _smartdrawers.push(_smartdrawer);
            }
        }

        return _smartdrawers;
    }

    ngOnInit(){
        this._RefreshDevices();
    }

    ngOnChanges(changes: SimpleChanges) : void {
        if ('_service' in changes){     // only refresh on service change
            this._RefreshDevices();

            if (this._service_subscription){
                this._service_subscription.unsubscribe();
                this._service_subscription = null;
            }
    
            this._service_subscription = this._service.OnRefresh.subscribe(
            data => {
                if (!this.IsDirty){
                    this._RefreshDevices();
                }
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(){
        super.OnDestroy();

        if (this._service_subscription){
            this._service_subscription.unsubscribe();
            this._service_subscription = null;
        }        
    }

    /****************************/
    /* EVENT HANDLERS           */
    /****************************/

    private _expand = false;
    get ExpandSmartDrawers(){
        return this._expand;
    }

    @ViewChild('devices', { static: false }) _devices: ElementRef;
    OnExpandSmartDrawers(){
        this._expand = !this._expand;

        if (this._devices){
            let _devices = this._devices.nativeElement;
            if (this._expand){
                _devices.style.height = _devices.scrollHeight + 'px'; 
                setTimeout(() => {
                    _devices.style.height = 'auto';
                }, 200);                
            }
            else {
                _devices.style.height = _devices.scrollHeight + 'px';
                _devices.offsetHeight;                
                _devices.style.height = '0px';     
            }    
        }

        this.change.markForCheck();
    }

    OnAddDevice(){
        this._isdirty = true;

        let _device = new SmartDrawerDevice(null, this.data);
        if (_device){
            _device.rasppi = this._service;
            _device.status = 'AC'
        }

        this.EditDevice = _device;
        this.LoadSmartDrawerForm();
        this.EditDevice['operation'] = 'add';

        let _idx = this._smartdrawers.indexOf(_device);
        if (_idx == -1){
            this._smartdrawers.unshift(this.EditDevice);
        }

        this._expand = true;
        this.change.markForCheck();
    }

    OnSetDevice(device){
        this.EditDevice = device;
        this.LoadSmartDrawerForm();
        this.EditDevice['operation'] = 'set';

        this.change.markForCheck();
    }

    /**
     * Devuelve true si la lista local de smartdrawers DIFIERE de la del
     * service (hay cambios pendientes), false si estan sincronizadas.
     *
     * OJO: usar el getter PRIVADO _PlaceSmartDrawers (from this._service)
     * y NO el publico PlaceSmartDrawers (que es solo this._smartdrawers
     * con un wrapper) — comparar this._smartdrawers consigo mismo siempre
     * era true y _isdirty se quedaba descuadrado del estado real.
     */
    private _SmartDrawersDirty(){
        let _placesmartdrawers = this._PlaceSmartDrawers;
        if (_placesmartdrawers.length != this._smartdrawers.length){
            return true;
        }
        return !_placesmartdrawers.every(_smartdrawer => {
            return this._smartdrawers.includes(_smartdrawer);
        });
    }

    private _DelDevice(_device){
        _device.status = 'DE';

        let _idx = this._smartdrawers.indexOf(_device);
        if (_idx != -1){
            this._smartdrawers.splice(_idx, 1);
        }

        this.EditDevice = null;
        // Tras borrar el dispositivo la lista local cambio respecto al
        // service — el helper devolvera true (dirty) y el parent detecta
        // los cambios pendientes. Antes el helper se llamaba "Updated" y
        // devolvia true cuando estaban IGUALES, asi que aqui se asignaba
        // false y el padre no veia que habia algo que persistir.
        this._isdirty = this._SmartDrawersDirty();
        this.change.markForCheck();
    }

    async OnDelDevice(device){
        const alert = this.alertCtrl.alert({
            header: this.lang.tr('@place_smartdrawer_delete_confirm'),
            message: this.lang.tr('@place_smartdrawer_delete_message', [ device.name || this.lang.tr('@place_device_smartdrawer_noname') ]),
            buttons: [{
                text: this.lang.tr('@yes'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(true);
                    }, 100);
                    return false;
                }
            }, {
                text: this.lang.tr('@no'),
                handler: () => {
                    setTimeout(() => {
                        alert.dismiss(false);
                    }, 100);
                    return false;
                }
            }]
        });

        alert.onDidDismiss().then(async (detail:any) => {
            if (detail.data == true){
                this._DelDevice(device);
            }
        });

        await alert.present();
    }

    /****************************/
    /* APPLY CHANGES            */
    /****************************/

    private _ApplySmartDrawerForm(){
        this.EditDevice.name = this.SmartDrawerForm.get('name').value;
        this.EditDevice.model = this.SmartDrawerForm.get('model').value?.value;

        if (this.EditDevice.name){
            this.EditDevice.name = this.EditDevice.name.trim();
        }

        // Ensure the device is marked for commit
        // New devices (without objid) are automatically marked as ToInsert in constructor
        // Existing devices need to be marked as ToUpdate when modified
        if (this.EditDevice.objid && !this.EditDevice.ToInsert && !this.EditDevice.ToUpdate){
            this.EditDevice.ToUpdate = true;
        }
    }

    /****************************/
    /* COMMIT ACTIONS           */
    /****************************/

    OnAcceptDevice(){
        this._ApplySmartDrawerForm();
        this.SmartDrawerForm.markAsPristine();

        // Mark existing devices for update. New devices ya tienen
        // _toInsert=true en el constructor de BaseObject cuando objid es
        // null, asi que no hace falta forzarlo (los Copilot review caught
        // que era brittle y tocaba campo privado).
        if (this.EditDevice && this.EditDevice.objid){
            this.EditDevice.ToUpdate = true;
        }

        this.EditDevice = null;
        this.IsDirty = true;
    }

    OnCancelDevice(){
        this.SmartDrawerForm.markAsPristine();

        if (this.EditDevice['operation'] == 'add'){
            this._DelDevice(this.EditDevice);
        }

        this.EditDevice = null;
    }
}
